import * as ents from '@attain-apps/entities'
import { PartialId } from '@attain-apps/string-builder'

import type * as entities from '../entities'
import type * as schema from '../schema'
import type * as snapshots from '../snapshots'

/**
 * Produces an `editingPlugin.Config.newCreation` callback that pre-assigns a
 * record id the moment a new record draft is started, but **only** for
 * models that have at least one Files-typed attribute.
 *
 * This is needed because the `putData` mutation never returns the persisted
 * record's values (see {@link ../effects/upload-pending-files-on-save}), so
 * without a client-known id upfront, pending file uploads staged before the
 * record is saved would have nowhere to attach to. Models without a Files
 * attribute are left completely untouched — the backend continues to
 * auto-assign their id as before.
 */
export function createNewCreationForFiles(
  entitiesEc: entities.Collection,
  snapshotsEc: snapshots.Collection,
  schemaFeature: schema.Feature,
): (
  input: Partial<ents.core.dms.data.Record.Input> | undefined,
) => ents.core.dms.data.Record.Input {
  return input => {
    const entityId = entitiesEc.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal()
    const snapshotId = snapshotsEc.plugins.currentEntityPlugin.feature.selectors
      .getId()
      .asSignal()
    const modelId = ents.core.dms.models.Entity.Id.isId(entityId)
      ? entityId
      : ents.core.dms.models.Snapshot.Id.isId(snapshotId)
        ? snapshotId
        : undefined

    const hasFilesAttribute = Boolean(
      modelId &&
        schemaFeature.selectors.schema
          .asSignal()
          ?.getAttributesOf(modelId)
          .some(attribute =>
            ents.core.dms.data.Value.Type.Files.isFiles(attribute.type),
          ),
    )

    const values = { ...input?.values }
    if (hasFilesAttribute && !values[ents.core.dms.data.Record.Id.KEY]) {
      values[ents.core.dms.data.Record.Id.KEY] = PartialId.Custom.create()
    }

    return { ...input, values }
  }
}
